import React, { useEffect } from 'react'
import { getInputs } from '../DeckOfficer.model';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { loadSpeedConsumptionObject, Get } from 'VesselMaster/vesselMaster.hooks';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import * as Yup from "yup";
import { dateTimeFormat, compareObjectIgnoreType, handleServerResponse, useSafeFormikContext, saveEditForm, checkValidObject, checkValidArray, isFormUnchanged } from 'GenericForms/Helper';
import env from 'environment_system/env_system';
import apiGlobal from 'global/api.global';
import { useFormik } from 'formik';
import { Button, Card, CardBody, CardFooter, CardHeader, Col, Input, Label, Row } from 'reactstrap';
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import ErrorComponent from 'Components/ErrorComponent';
import ToolTip from 'Components/ToolTip';
import { TooltipMsg } from 'Components/ToolTipMessage';
import ErrorTooltip from 'Components/ErrorTooltip';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import { AlertColourTypeConstant, AlertTypeConstant, EventPrecedence, FUELLCV, FuelTypes } from "../../../shared/constants";
import { useState } from "react";
import AlertPopup from "Alerts/AlertPopup";
import { getAlertMessage } from "Alerts/AlertMessages";
import { calculateTotalDOeq } from 'Simulator/CIIProjection/CommonFunctions';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import PopUp from 'Components/PopUp';
import { getAuthData } from 'utils/auth/authUtils';
import { RootState } from 'index';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
import FormLoading from 'Components/FormLoading';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';
interface CPFormType {
    toggleTab?: any;
    setErrorMessage?: any
}

const CpWarrantyComponent = ({
    toggleTab,
    setErrorMessage,
}: CPFormType) => {
    const { previousDeckTabIndex, editReportBool, setPreviousDeckTabIndex, discardwarning } = useEditReport();
    /** State variables start */
    useDocumentTitle("CP Warranties Report - ecoSAIL");
    const { VesselID, ReportID, VoyageID, CurrentReport, PreviousReport, reportingLoading, ConfigurationLimits, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    let { inputs } = getInputs("cp_warranties");
    const [alertBool, setAlertBool] = useState<boolean>(false);
    const [alertMessage, setAlertMessage] = useState<string>(null);
    const [fieldLabel, setFieldLabel] = useState<string>(null);
    const [fieldValue, setFieldValue] = useState<any>(null);
    const [alertColourType, setAlertColourType] = useState<any>(null);
    const [alertType, setAlertType] = useState<string>(null);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [saveBeforeChangePopUp, setStateSaveBeforeChangePopUp] = useState<boolean>(false);
    const [disable, setDisable] = useState<boolean>(false);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const CPWarrantiesSpeedCurrentReport = CurrentReport?.data?.deck_officer?.cp_warranties_speed ?? null;
    const CPWarrantiesFuelCurrentReport = CurrentReport?.data?.deck_officer?.cp_warranties_fuel ?? null;
    const currentVesselReport = CurrentReport?.data?.vessel_reporting_information ?? null;
    const previousVesselReport = PreviousReport?.data?.vessel_reporting_information ?? null;
    const fuelTypes = ConfigurationLimits?.vessel_fuel_information || [];
    /** State variables end */

    /** useQueries */
    const {
        data: lastCPWarranty,
        isLoading: lastCPWarrantyLoading,
        isError: lastCPWarrantyError,
    } = useQuery(
        [queryKeyes.vessel.lastCPWarranties.key, VesselID, ReportID],
        async () => {
            return await Get(queryKeyes.vessel.lastCPWarranties.url(VesselID, ReportID), VesselID, ReportID);
        },
        {
            enabled: true,
            staleTime: Infinity,
        }
    );
    /** useQueries end */

    /** Assign initial values to CP Warranties' formik object */
    const CpFormObjectInitialValues = () => {
        if (
            editInitialValues &&
            (
                editInitialValues?.deck_officer?.cp_warranties_speed?.id > 0 ||
                editInitialValues?.deck_officer?.cp_warranties_fuel?.[0]?.id > 0
            )
        ) {
            return {
                speed: {
                    ...(editInitialValues?.deck_officer?.cp_warranties_speed ?? {}),
                    vessel: VesselID,
                    vessel_reporting_information: ReportID,
                    voyage_information: VoyageID,
                },
                fuel: Array.isArray(editInitialValues?.deck_officer?.cp_warranties_fuel)
                    ? editInitialValues.deck_officer.cp_warranties_fuel.map((fuel: any) => ({
                        ...fuel,
                        vessel: VesselID,
                        vessel_reporting_information: ReportID,
                        voyage_information: VoyageID,
                    }))
                    : [],
            };
        } else if (checkValidObject(CPWarrantiesSpeedCurrentReport) && checkValidArray(CPWarrantiesFuelCurrentReport)) {
            return {
                speed: {
                    id: CPWarrantiesSpeedCurrentReport?.id ?? null,
                    ordered_speed: CPWarrantiesSpeedCurrentReport?.ordered_speed ?? null,
                    date_of_change: CPWarrantiesSpeedCurrentReport?.date_of_change ?? null,
                    is_there_any_changes_since_last_report:
                        CPWarrantiesSpeedCurrentReport?.is_there_any_changes_since_last_report ?? false,
                    vessel: VesselID,
                    vessel_reporting_information: ReportID,
                    voyage_information: VoyageID,
                },
                fuel: CPWarrantiesFuelCurrentReport.map((fuel: any) => ({
                    id: fuel.id ?? null,
                    fuel_type_precedence_id: fuel.fuel_type_precedence_id ?? null,
                    ordered_fuel_oil_consumption: fuel.ordered_fuel_oil_consumption ?? null,
                    vessel: VesselID,
                    voyage_information: VoyageID,
                    vessel_reporting_information: ReportID,
                    fuel_type: fuel.fuel_type ?? null,
                })),
            };
        } else if (checkValidObject(lastCPWarranty) && currentVesselReport?.event_precedence_id !== EventPrecedence.BEGINNING_OF_SEA_PASSAGE) {
            return {
                speed: {
                    ordered_speed: lastCPWarranty?.speed?.ordered_speed ?? null,
                    date_of_change: lastCPWarranty?.speed?.date_of_change ?? null,
                    is_there_any_changes_since_last_report: false,
                    vessel: VesselID,
                    vessel_reporting_information: ReportID,
                    voyage_information: VoyageID,
                },
                fuel: Array.isArray(lastCPWarranty?.fuel)
                    ? lastCPWarranty.fuel.map((fuel: any) => ({
                        fuel_type_precedence_id: fuel.fuel_type_precedence_id ?? null,
                        ordered_fuel_oil_consumption: fuel.ordered_fuel_oil_consumption ?? null,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                        fuel_type: fuel.fuel_type ?? null,
                    }))
                    : [],
            };
        } else {
            return {
                speed: {
                    ordered_speed: null,
                    date_of_change: null,
                    is_there_any_changes_since_last_report: true,
                    vessel: VesselID,
                    vessel_reporting_information: ReportID,
                    voyage_information: VoyageID,
                },
                fuel: Array.isArray(fuelTypes)
                    ? fuelTypes.map((fuel: any) => ({
                        fuel_type_precedence_id: fuel.fuel_type_precedence_id ?? null,
                        ordered_fuel_oil_consumption: null as number | null,
                        vessel: VesselID,
                        voyage_information: VoyageID,
                        vessel_reporting_information: ReportID,
                        fuel_type: fuel.fuel_type ?? null,
                    }))
                    : [],
            };
        }
    };

    /** CP Warranties' formik object validation schema */
    const validationSchemaCpWarranties =
        Yup.object().shape({
            speed: Yup.object().shape({
                ordered_speed: Yup.string()
                    .when((_value: any, schema: any) => {
                        return isThereChanges
                            ? schema.required(commonValidationMessages.required)
                                .matches(/^\d+(\.\d+)?$/, commonValidationMessages.validNumber)
                                .matches(
                                    /^\d{0,2}(?:\.\d{1,4})?$/,
                                    commonValidationMessages.before2after4
                                ) : schema.nullable();
                    }),
                lng_foe: Yup.string()
                    .matches(
                        /^\d{0,2}(?:\.\d{1,4})?$/,
                        `${commonValidationMessages.before2after4}`
                    )
                    .nullable(),
                is_there_any_changes_since_last_report: Yup.boolean(),
                date_of_change: Yup.date().when((_value: any, schema: any) => {
                    return isThereChanges
                        ? schema.max(
                            new Date(dateTimeFormat(currentVesselReport?.local_time)),
                            commonValidationMessages.maxDateCurrentRecord
                        )
                            .min(
                                new Date(dateTimeFormat(previousVesselReport?.local_time)),
                                `${commonValidationMessages.minDateLastRecord} (${dateTimeFormat(previousVesselReport?.local_time, false, false, true)})`
                            )
                            .required(commonValidationMessages.required)
                        : schema.nullable();
                })
            }),
            fuel: Yup.array().of(
                Yup.object().shape({
                    ordered_fuel_oil_consumption: Yup.string()
                        .when((_parent, schema) => {
                            return isThereChanges
                                ? schema
                                    .required(commonValidationMessages.required)
                                    .matches(/^\d+(\.\d+)?$/, commonValidationMessages.validNumber)
                                    .matches(/^\d{0,2}(\.\d{1,4})?$/, commonValidationMessages.before2after4)
                                : schema.nullable();
                        }),
                })
            ),
        })

    /** CP Warranties' formik object */
    const CPWarrantiesFormik: any = useFormik({
        enableReinitialize: true,
        initialValues: CpFormObjectInitialValues(),
        validationSchema: env?.form_validation === true ? validationSchemaCpWarranties : null,
        onSubmit: async (values: any, actions: any) => {
            if (env?.form_validation === false || isFormUnchanged(CPWarrantiesFormik.initialValues, values)) {
                setErrorMessage(null)
                toggleTab(previousDeckTabIndex + 1);
                return;
            }
            try {
                actions.setSubmitting(true);

                const responseArray: any = [];
                // Handle Speed Data (PUT if id exists, POST if not)
                if (CPWarrantiesFormik?.values) {
                    responseArray.push(
                        apiGlobal.post(`${queryKeyes.vessel.CPWarrantiesFuel.url()}`, values)
                    );
                }
                const response = await handleServerResponse(responseArray);

                if (response === true) {
                    dispatch(getCurrentReportAction(VesselID, ReportID));
                    setRefreshKey(refreshKey + 1);
                    if (env?.form_validation === true) {
                        setErrorMessage(null);
                        toggleTab(previousDeckTabIndex + 1);
                    }
                }
            } catch (error) {
                console.error("Error submitting CP Warranties data:", error);
                setErrorMessage("An unexpected error occurred. Please try again.");
            } finally {
                actions.setSubmitting(false);
            }
        },
    });

    /** Check if there are any changes since last report */
    const isThereChanges = CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report ||
        (!CPWarrantiesFuelCurrentReport && !CPWarrantiesSpeedCurrentReport && !lastCPWarranty);

    /** load speed and consumption by cp warranties ordered speed */
    const { data: SpeedAndConsumption } = useQuery(
        [queryKeyes.vessel.SpeedConsumptionObject.key, VesselID, ReportID, CPWarrantiesFormik?.values],
        async () => {
            return await loadSpeedConsumptionObject(VesselID, ReportID, CPWarrantiesFormik?.values?.speed?.ordered_speed);
        },
        {
            enabled: true,
            staleTime: Infinity,
        }
    );

    const handleSaveAndSwitchTab = async (formik: any) => {
        await formik.submitForm();
        formik.initialValues = formik.values;
        formik.resetForm({ values: formik.initialValues });
        setRefreshKey(refreshKey + 1);
        setStateSaveBeforeChangePopUp(false);
    }
    const handleNoSaveAndSwitchTab = (formik: any) => {
        formik.setValues(formik.initialValues);
        setRefreshKey(refreshKey + 1);
        setStateSaveBeforeChangePopUp(false);
    };
    /** useEffect */
    useEffect(() => {
        if (editValues) {
            const updatedFuel = (CPWarrantiesFormik?.values?.fuel || []).map((fuel: any) => ({
                ...fuel,
                report_index: null,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
                vessel: VesselID,
                user: getAuthData('user_id'),
            }));
            const updatedSpeed = {
                ...CPWarrantiesFormik?.values?.speed,
                report_index: null,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID,
                vessel: VesselID,
                user: getAuthData('user_id'),
            };
            setEditValues({
                ...editValues,
                deck_officer: {
                    ...editValues.deck_officer,
                    cp_warranties_speed: updatedSpeed,
                    cp_warranties_fuel: updatedFuel, // ARRAY of objects
                },
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [CPWarrantiesFormik.values]);
    useEffect(() => {
        setRefreshKey(prev => prev + 1); // Optional: if your form depends on this key
    }, [previousVesselReport]);
    /**
     * Reset the form errors when the save before change pop up is closed
     */
    useEffect(() => {
        if (saveBeforeChangePopUp && CPWarrantiesFormik?.setErrors) {
            CPWarrantiesFormik.setErrors({});
        }
    }, [saveBeforeChangePopUp, CPWarrantiesFormik]);
    useEffect(() => {
        CPWarrantiesFormik.values = CpFormObjectInitialValues();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastCPWarranty, CPWarrantiesFuelCurrentReport, CPWarrantiesSpeedCurrentReport, fuelTypes]);
    /** useEffect end */

    if (lastCPWarrantyLoading || reportingLoading) {
        return <FormLoading />;
    } else if (lastCPWarrantyError || reportingError) {
        return <ErrorComponent message="Error loading required data!" />;
    } else {
        return (
            <React.Fragment>
                <Card className="p-0 mb-0 border-0">
                    <CardHeader className="p-2">
                        <div className="text-center">
                            <Row>
                                <Col>
                                    <h4 className="page_title pos-start mb-0">CP Warranties</h4>
                                    <p className="card-title-desc pos-start">
                                        All readings since last report
                                    </p>
                                </Col>
                                <Col>
                                    <ReportDetailsHeader />
                                </Col>
                            </Row>
                        </div>
                    </CardHeader>
                    <form className="needs-validation" autoComplete="off" noValidate onSubmit={CPWarrantiesFormik.handleSubmit} key={refreshKey}>
                        <CardBody className="px-2 py-0 mt-2">
                            <Col sm="4" className="mt-4 mb-3">
                                <div className="form-check">
                                    <Label for="is_there_any_changes_since_last_report">
                                        Is there any change since last report?
                                        <i
                                            className="bx bx-info-circle ml-2p"
                                            id="formCheck1"
                                        />
                                    </Label>
                                    <input
                                        name="speed.is_there_any_changes_since_last_report"
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_there_any_changes_since_last_report"
                                        checked={CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report || false}
                                        onChange={async (e: any) => {
                                            const isChecked = e.target.checked;
                                            await CPWarrantiesFormik?.handleChange(e);
                                            if (!isChecked) {
                                                const areValuesEqual = compareObjectIgnoreType(
                                                    {
                                                        ...CPWarrantiesFormik?.values,
                                                        speed: {
                                                            ...CPWarrantiesFormik?.values?.speed,
                                                            is_there_any_changes_since_last_report: e.target.checked
                                                        }
                                                    },
                                                    CPWarrantiesFormik?.initialValues);
                                                setStateSaveBeforeChangePopUp(!areValuesEqual);
                                            }
                                            setDisable(Object.keys(CPWarrantiesFormik?.errors || {}).length > 0);
                                        }}
                                    />
                                    <ToolTip
                                        target="formCheck1"
                                        message={`${TooltipMsg.CP_Warranties.filter(
                                            (item: any) => item.target === "formCheck1"
                                        ).map((tool: any) => {
                                            return tool.message;
                                        })}`}
                                    />
                                </div>
                            </Col>
                            <PopUp
                                state={saveBeforeChangePopUp}
                                setState={setStateSaveBeforeChangePopUp}
                                title="Unsaved Changes"
                                body={
                                    <div>
                                        <p>{disable ? "Incorrect values detected. Please check the inputs and continue." : "There are unsaved changes. Do you want to save them?"}</p>
                                        <div className='ele_row1 d-flex justify-content-end gap-2'>
                                            {disable ?
                                                <Button
                                                    type="button"
                                                    color="primary"
                                                    className="btn_size_cstm"
                                                    onClick={() => {
                                                        setStateSaveBeforeChangePopUp(false);
                                                        CPWarrantiesFormik?.setFieldValue("speed.is_there_any_changes_since_last_report", true);
                                                    }}
                                                >
                                                    Ok
                                                </Button> :
                                                <React.Fragment>
                                                    <Button
                                                        type="button"
                                                        color="primary"
                                                        className="btn_size_cstm"
                                                        onClick={async () => {
                                                            if (editReportBool) {
                                                                await CPWarrantiesFormik.setFieldTouched("speed.date_of_change", true);
                                                                const errors = await CPWarrantiesFormik.validateForm();
                                                                if (editReportBool && Object.keys(errors).length === 0) {
                                                                    saveEditForm(editSubmit, toggleTab, previousDeckTabIndex, setPreviousDeckTabIndex);
                                                                }
                                                            } else {
                                                                handleSaveAndSwitchTab(CPWarrantiesFormik)
                                                            }
                                                        }}
                                                        disabled={disable || editIsSubmitting}
                                                    >
                                                        Yes
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        color="danger"
                                                        className="btn_size_cstm"
                                                        onClick={() => handleNoSaveAndSwitchTab(CPWarrantiesFormik)}
                                                    >
                                                        No
                                                    </Button>
                                                </React.Fragment>
                                            }
                                        </div>
                                    </div>
                                }
                                size="sm"
                                closeButton={false}
                            />
                            <Row className="mb-2">
                                <Col sm={3} className="mb-3">
                                    <Label className="asteric mb-0" for="ordered_speed">
                                        Ordered speed
                                        <i className="bx bx-info-circle ml-2p" id="ordered_speed_msg"></i>
                                    </Label>
                                    <ToolTip
                                        target="ordered_speed_msg"
                                        message={`${TooltipMsg.CP_Warranties.filter(
                                            (item: any) => item.target === "ordered_speed_msg"
                                        ).map((tool: any) => {
                                            return tool.message;
                                        })}`}
                                    />
                                    <div className="input-group">
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            type="text"
                                            name="speed.ordered_speed"
                                            className={`form-control max-width-7 text-right 
                                                ${((CPWarrantiesSpeedCurrentReport || lastCPWarranty) &&
                                                    !CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report)
                                                && "cursor-not-allowed"}`}
                                            id="ordered_speed"
                                            disabled={
                                                ((!CPWarrantiesSpeedCurrentReport || !lastCPWarranty) === true ||
                                                    currentVesselReport?.event_precedence_id === EventPrecedence.BEGINNING_OF_SEA_PASSAGE) ? false :
                                                    (!CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report)
                                            }
                                            onBlur={(e: any) => {
                                                CPWarrantiesFormik?.handleBlur(e);
                                                CPWarrantiesFormik?.handleChange(e);
                                            }}
                                            defaultValue={CPWarrantiesFormik?.values?.speed?.ordered_speed ?? null}
                                        />
                                        <div className="input-group-text round_border">knots</div>
                                    </div>
                                    {CPWarrantiesFormik?.errors?.speed?.ordered_speed &&
                                        CPWarrantiesFormik?.touched?.speed?.ordered_speed &&
                                        env?.form_validation === true && !discardwarning && (
                                            <ErrorTooltip
                                                target="ordered_speed"
                                                message={CPWarrantiesFormik?.errors?.speed?.ordered_speed}
                                                open={!!CPWarrantiesFormik?.errors?.speed?.ordered_speed}
                                            />
                                        )}
                                </Col>
                                <Col sm={3}>
                                    <div>
                                        <Label className="asteric mb-0" for="date_of_change">
                                            Date & time of change (Local)
                                            <i
                                                className="bx bx-info-circle ml-2p"
                                                id="date_time_of_change_msg"
                                            ></i>
                                        </Label>
                                        <ToolTip
                                            target="date_time_of_change_msg"
                                            message={`${TooltipMsg.CP_Warranties.filter(
                                                (item: any) =>
                                                    item.target === "date_time_of_change_msg"
                                            ).map((tool: any) => {
                                                return tool.message;
                                            })}`}
                                        />
                                        <Input
                                            onFocus={(e) => e.target.select()}
                                            name="speed.date_of_change"
                                            type="datetime-local"
                                            id="date_of_change"
                                            value={dateTimeFormat(CPWarrantiesFormik?.values?.speed?.date_of_change, false, true)}
                                            className={`datetimepicker text-uppercase mb-2 mt-0 
                                                ${((CPWarrantiesSpeedCurrentReport || lastCPWarranty) &&
                                                    !CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report)
                                                && "cursor-not-allowed"}`}
                                            min={dateTimeFormat(previousVesselReport?.local_time)}
                                            max={dateTimeFormat(currentVesselReport?.local_time)}
                                            onBlur={CPWarrantiesFormik?.handleBlur}
                                            disabled={
                                                ((!CPWarrantiesSpeedCurrentReport || !lastCPWarranty) === true ||
                                                    currentVesselReport?.event_precedence_id === EventPrecedence.BEGINNING_OF_SEA_PASSAGE) ? false :
                                                    !CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report
                                            }
                                            onChange={(e: any) => CPWarrantiesFormik?.handleChange(e)}
                                        />
                                    </div>
                                    {CPWarrantiesFormik?.errors?.speed?.date_of_change &&
                                        CPWarrantiesFormik?.touched?.speed?.date_of_change &&
                                        env?.form_validation === true && !discardwarning && (
                                            <ErrorTooltip
                                                target={`date_of_change`}
                                                message={CPWarrantiesFormik?.errors?.speed?.date_of_change}
                                                open={CPWarrantiesFormik?.errors?.speed?.date_of_change ? true : false}
                                            />
                                        )}
                                </Col>
                            </Row>
                            <Row>
                                <Col sm={{ size: 8 }}>
                                    <div key="fuel_key" className="table-responsive ">
                                        <table className="table mb-2">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="p2 align-middle sr-no-width">#</th>
                                                    {inputs
                                                        ?.filter(
                                                            (table: any) => table.table_columns === true
                                                        )
                                                        ?.sort(
                                                            (a: any, b: any) =>
                                                                a.table_columns_sequence -
                                                                b.table_columns_sequence
                                                        )
                                                        ?.map(({ ...props }, index: number) => {
                                                            return (
                                                                <th
                                                                    key={index}
                                                                    className={
                                                                        props.label === "Fuel type"
                                                                            ? "text-left align-middle p-2"
                                                                            : props.className +
                                                                            " align-middle p-2"
                                                                    }
                                                                >
                                                                    {props.label}
                                                                </th>
                                                            );
                                                        })}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {Array.isArray(CPWarrantiesFormik?.values?.fuel) && CPWarrantiesFormik?.values?.fuel
                                                    ?.sort((a: any, b: any) => a.id - b.id)
                                                    ?.map((vesselFuelType: any, index: number) => (
                                                        <tr key={index}>
                                                            <td className="p-2 align-middle text-center">
                                                                {index + 1}
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                {
                                                                    fuelTypes?.filter(
                                                                        (fuel: any) =>
                                                                            fuel.fuel_type ===
                                                                            vesselFuelType.fuel_type
                                                                    )[0]?.fuel_type_name
                                                                }
                                                            </td>
                                                            <td className="p-2 align-middle">
                                                                <div className="input-group">
                                                                    <Input
                                                                        onFocus={(e) => e.target.select()}
                                                                        name={`fuel.${index}.ordered_fuel_oil_consumption`}
                                                                        type="text"
                                                                        className={`form-control max-width-7 text-right
                                                                            ${((CPWarrantiesFuelCurrentReport || lastCPWarranty) &&
                                                                                !CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report)
                                                                            && "cursor-not-allowed"}`}
                                                                        id={`ordered_fuel_oil_consumption_${index}`}
                                                                        disabled={
                                                                            ((!CPWarrantiesFuelCurrentReport && !lastCPWarranty) === true ||
                                                                                currentVesselReport?.event_precedence_id === EventPrecedence.BEGINNING_OF_SEA_PASSAGE) ? false :
                                                                                !CPWarrantiesFormik?.values?.speed?.is_there_any_changes_since_last_report
                                                                        }
                                                                        onBlur={async (e: any) => {
                                                                            await calculateTotalDOeq(
                                                                                {},
                                                                                CPWarrantiesFormik?.values?.fuel,
                                                                                () => { },
                                                                                null,
                                                                                false,
                                                                                "fuel_type_precedence_id",
                                                                                "ordered_fuel_oil_consumption",
                                                                                false
                                                                            ).then((res: any) => {
                                                                                let DOeqSpeedAndConsumption;
                                                                                if (SpeedAndConsumption?.fuel_type === FuelTypes.DIESEL_OIL) {
                                                                                    DOeqSpeedAndConsumption = SpeedAndConsumption?.fuel_consumption
                                                                                } else {
                                                                                    DOeqSpeedAndConsumption =
                                                                                        ((SpeedAndConsumption?.fuel_consumption ?? 0) *
                                                                                            (res?.FuelLCVMaster?.filter((item: any) => item.fuel_type_precedence_id === SpeedAndConsumption?.fuel_type)[0]?.lcv * 1000000) /
                                                                                            FUELLCV.DO)
                                                                                }
                                                                                CPWarrantiesFormik.handleBlur(e)
                                                                                if (env?.alerts && res?.DOeq > DOeqSpeedAndConsumption + 3) {
                                                                                    setAlertMessage(
                                                                                        getAlertMessage("CpWarranties", "ordered_fuel_oil_consumption_gt_3", "Fuel oil consumption", res.DOeq, `${DOeqSpeedAndConsumption}–${DOeqSpeedAndConsumption + 3}`)
                                                                                    );
                                                                                    setAlertColourType(AlertColourTypeConstant.INFO);
                                                                                    setAlertType(AlertTypeConstant.INFO_ONLY);
                                                                                    setAlertBool(true);
                                                                                    setFieldLabel("Fuel oil consumption");
                                                                                    setFieldValue(res.DOeq);
                                                                                } else if (env?.alerts && res.DOeq < DOeqSpeedAndConsumption - 3) {
                                                                                    setAlertMessage(
                                                                                        getAlertMessage("CpWarranties", "ordered_fuel_oil_consumption_lt_3", "Fuel oil consumption", res.DOeq, `${DOeqSpeedAndConsumption}–${DOeqSpeedAndConsumption + 3}`)
                                                                                    );
                                                                                    setAlertColourType(AlertColourTypeConstant.INFO);
                                                                                    setAlertType(AlertTypeConstant.INFO_ONLY);
                                                                                    setAlertBool(true);
                                                                                    setFieldLabel("Fuel oil consumption");
                                                                                    setFieldValue(res.DOeq);
                                                                                }
                                                                            })
                                                                        }}
                                                                        onChange={CPWarrantiesFormik?.handleChange}
                                                                        value={CPWarrantiesFormik?.values?.fuel?.[index]?.ordered_fuel_oil_consumption ?? null}
                                                                    />
                                                                    <div className="input-group-text round_border">
                                                                        mt/day
                                                                    </div>
                                                                </div>
                                                                {CPWarrantiesFormik?.errors?.fuel &&
                                                                    CPWarrantiesFormik?.touched?.fuel &&
                                                                    CPWarrantiesFormik?.touched?.fuel?.[index]
                                                                        ?.ordered_fuel_oil_consumption &&
                                                                    CPWarrantiesFormik?.errors?.fuel?.[index]
                                                                        ?.ordered_fuel_oil_consumption &&
                                                                    env?.form_validation === true && !discardwarning && (
                                                                        <ErrorTooltip
                                                                            target={`ordered_fuel_oil_consumption_${index}`}
                                                                            message={
                                                                                CPWarrantiesFormik?.errors?.fuel?.[index]
                                                                                    ?.ordered_fuel_oil_consumption
                                                                            }
                                                                            open={
                                                                                CPWarrantiesFormik?.errors?.fuel &&
                                                                                    CPWarrantiesFormik?.errors?.fuel?.[index]
                                                                                        ?.ordered_fuel_oil_consumption
                                                                                    ? true
                                                                                    : false
                                                                            }
                                                                        />
                                                                    )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                        {alertBool &&
                            <AlertPopup
                                state={alertBool}
                                setState={setAlertBool}
                                alertMessage={alertMessage}
                                setAlertMessage={setAlertMessage}
                                AlertColourType={alertColourType}
                                setAlertColourType={setAlertColourType}
                                alertType={alertType}
                                setAlertType={setAlertType}
                                formTitle={'CP Warranties'}
                                fieldLabel={fieldLabel}
                                fieldValue={fieldValue}
                            />
                        }
                        <CardFooter className="p-2 py-3">
                            <Row className="d-flex align-items-center flex-row-reverse">
                                <Col lg={10}>
                                    <div className="d-flex flex-wrap gap-3 justify_right">
                                        {/* {editReportBool &&
                                            <Button
                                                type="reset"
                                                color="danger"
                                                className="btn_size_cstm"
                                                onClick={() => {
                                                    setEditValues({
                                                        ...editValues,
                                                        deck_officer: {
                                                            ...editValues?.deck_officer,
                                                            cp_warranties_fuel: editInitialValues?.deck_officer?.cp_warranties_fuel,
                                                            cp_warranties_speed: editInitialValues?.deck_officer?.cp_warranties_speed,
                                                        }
                                                    })
                                                    CPWarrantiesFormik?.resetForm();
                                                    setRefreshKey(refreshKey + 1);
                                                }}
                                            >
                                                Reset
                                            </Button>
                                        } */}
                                        <Button
                                            type={editReportBool ? "button" : "submit"}
                                            color="primary"
                                            className="btn_size_cstm"
                                            disabled={CPWarrantiesFormik?.isSubmitting || editIsSubmitting}
                                            onClick={async () => {
                                                await CPWarrantiesFormik.setFieldTouched("speed.date_of_change", true);
                                                const errors = await CPWarrantiesFormik.validateForm();
                                                if (editReportBool && Object.keys(errors).length === 0) {
                                                    saveEditForm(editSubmit, toggleTab, previousDeckTabIndex);
                                                }
                                            }}
                                        >
                                            {(CPWarrantiesFormik?.isSubmitting || editIsSubmitting) && (
                                                <i className="ms-2 spinner-border spinner-border-sm text-light me-2" />
                                            )}
                                            Next <i className="bx bx-chevron-right ms-1" />
                                        </Button>
                                    </div>
                                </Col>
                                <Col lg={2}>
                                    <Button
                                        type="button"
                                        color="primary"
                                        className="btn_size_cstm"
                                        onClick={() => {
                                            toggleTab(previousDeckTabIndex - 1);
                                            setErrorMessage(null);
                                            CPWarrantiesFormik?.setErrors({});
                                        }}
                                    >
                                        <i className="bx bx-chevron-left me-1" /> Previous
                                    </Button>
                                </Col>
                            </Row>
                        </CardFooter>
                        <FormValuesDebug
                            values={[
                                CPWarrantiesFormik?.errors,
                                CPWarrantiesFormik?.touched,
                                CPWarrantiesFormik?.initialValues,
                                CPWarrantiesFormik?.values,
                            ]}
                        />
                    </form>
                </Card>
            </React.Fragment>
        )
    }
}

export default CpWarrantyComponent
